import type { ResendVerificationResult } from '@/lib/api';
import { CONTACT_EMAIL } from '@/lib/contact';

export type VerificationNote = { tone: 'ok' | 'bad'; message: string };

/**
 * What the buyer is told after asking for a verification link — one entry per
 * outcome `resendVerificationEmail()` can return, and the ONLY place this copy
 * exists.
 *
 * **A table, not a `try`/`catch`, on purpose.** Both surfaces that offer a
 * resend had one `catch` covering every failure and a success line in the happy
 * path, so the three ways the send can fail without throwing at the browser —
 * the address is already verified (a 200 that emails nothing), the provider
 * refused (502), the flow is unwired server-side (503) — were either invisible
 * or reported as "Verification email sent — check your inbox." Sending somebody
 * to wait on mail that was never accepted is this project's signature defect in
 * the one place the buyer cannot check: alert email is gated on
 * `users.email_verified`, so the cost of the lie is silence, forever.
 *
 * **One table, two surfaces.** `/settings/profile` and `VerifyEmailNudge` (which
 * appears on the dashboard, on saved searches and in the search filter panel)
 * both read it. They used to word this independently, which is how one of them
 * ends up honest and the other does not — a predicate that appears once cannot
 * disagree with itself.
 *
 * `tone: 'bad'` is not decoration. The old banner rendered every message in
 * green with a tick, including "Couldn't send the verification email" — a
 * failure wearing a success's clothes.
 */
export const VERIFICATION_NOTES: Record<ResendVerificationResult['status'], VerificationNote> = {
  sent: {
    tone: 'ok',
    message: 'Verification email sent — check your inbox for the link, and your spam folder too.',
  },
  'already-verified': {
    tone: 'ok',
    message:
      'Your email address is already verified, so we didn’t send another link. Alerts can reach you.',
  },
  'provider-refused': {
    tone: 'bad',
    message:
      'Our email provider refused the message, so nothing was sent and there is nothing to look for. ' +
      `Please try again in a few minutes — if it keeps happening, email ${CONTACT_EMAIL} and we'll verify you by hand.`,
  },
  'not-configured': {
    tone: 'bad',
    message:
      'Verification emails are switched off on our side right now, so no link was sent. That is our fault, not yours, ' +
      `and retrying won't help — email ${CONTACT_EMAIL} and we'll sort it out.`,
  },
  failed: {
    tone: 'bad',
    message:
      "We couldn't send the verification email just now, so nothing has arrived. Please try again in a minute.",
  },
};
